<!--
  - Copyright (c) 2022-2023. Haodong JU
  -->

<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <page-header class="py-4 pb-6">
      {{ $t('voucher') }}
      <template #action>
        <v-item-group
          v-model="tab"
          mandatory
        >
          <v-item v-slot="{active,toggle}">
            <v-btn
              :class="active?'amber lighten-4 black--text':''"
              :icon="$vuetify.breakpoint.mdAndDown"
              class="mr-2"
              outlined
              @click="toggle"
            >
              <v-icon
                v-if="$vuetify.breakpoint.mdAndDown"
                :color="active ? 'white' : 'black'"
                center
              >
                mdi-ticket-percent
              </v-icon>
              <template v-if="!$vuetify.breakpoint.mdAndDown">
                {{ $t('vouchersManage') }}
              </template>
            </v-btn>
          </v-item>
          <v-item v-slot="{active,toggle}">
            <v-btn
              :class="active?'amber lighten-4 black--text':''"
              :icon="$vuetify.breakpoint.mdAndDown"
              class="mr-2"
              outlined
              @click="toggle"
            >
              <v-icon
                v-if="$vuetify.breakpoint.mdAndDown"
                :color="active ? 'white' : 'black'"
                center
              >
                mdi-sale
              </v-icon>
              <template v-if="!$vuetify.breakpoint.mdAndDown">
                {{ $t('alreadySaledVouchers') }}
              </template>
            </v-btn>
          </v-item>
        </v-item-group>
      </template>
    </page-header>
    <v-tabs-items
      v-model="tab"
      class="mt-4"
    >
      <v-tab-item>
        <v-container>
          <div
            style="display: grid; grid-template-columns: repeat(auto-fill,minmax(250px,1fr)); grid-gap: 24px"
          >
            <v-card
              class="pa-4 d-flex align-center justify-center"
              color="#eee"
              elevation="0"
              height="320"
              @click="startEditCoupon(-1)"
            >
              <div class="text-center">
                <v-icon size="48">
                  mdi-plus
                </v-icon>
                <div>
                  {{ $t('addNewVouchers') }}
                </div>
              </div>
            </v-card>
            <coupon-info-card
              v-for="(c,i) in couponList"
              :key="c.dishId"
              :coupon-info="c"
              @click="startEditCoupon(i)"
              @delete="deleteAndReload(c.dishId)"
            />
          </div>
        </v-container>
      </v-tab-item>
      <v-tab-item>
        <ik-data-table
          entity-name="alreadySaledVouchers"
          :custom-on-row-click="showCouponDetail"
          :model="model"
          :required-date-value="dates"
          :show-title="false"
          :use-action="false"
          :use-date-filter="true"
          :use-default-action="false"
          :use-select="false"
          icon="mdi-cards"
        >
          <template #footer="{tableItems}">
            <v-card
              :class="!$vuetify.breakpoint.lgAndUp ? 'px-4' : 'px-6'"
              class="py-2 mr-4 text-body-2"
              color="amber lighten-4 black--text"
              dark
              outlined
            >
              {{
                $t('totalPrice')
              }}：{{ tableItems.reduce((sum, it) => sum + parseFloat(it.leftAmount), 0) | priceDisplay }}
            </v-card>
          </template>
          <template #right>
            <!-- Add operation buttons here if needed -->
          </template>
        </ik-data-table>
      </v-tab-item>
    </v-tabs-items>

    <nice-dialog v-model="showCouponDetailDialog">
      <v-container
        v-if="couponDetail"
        :class="!$vuetify.breakpoint.lgAndUp ? 'px-4' : 'px-6'"
        style="height: 98vh"
      >
        <page-header class="py-4 pb-6">
          {{ $t('vouchersUseRecord') }}
        </page-header>
        <div class="text-h5 font-weight-bold pl-2">
          {{ $t('voucherCode') }}: {{ couponDetail.longId }}
        </div>
        <span class="font-weight-regular pl-2 mt-2">{{ $t('value') }}: {{
          couponDetail.leftAmount | priceDisplay
        }}/{{ couponDetail.totalAmount | priceDisplay }}</span>

        <div class="text-body-1 pl-2">
          {{ $t('creatIn') }}: {{ couponDetail.createdAt | beautifulTime }}
        </div>
        <v-simple-table
          v-if="couponDetail.consumeLog.length>0"
          class="mt-4"
        >
          <thead>
            <tr>
              <th>{{ $t('orderNumber') }}</th>
              <th>{{ $t('time') }}</th>
              <th>{{ $t('useTheAmountOf') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="i in couponDetail.consumeLog"
              :key="i.id"
              @click="showOrder(i.orderId)"
            >
              <td>#{{ i.orderId }}</td>
              <td>{{ i.createdAt | beautifulTime }}</td>
              <td>{{ i.amount | priceDisplay }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <template v-else>
          <div class="pl-2 mt-4">
            {{ $t('Status') }}: {{ $t('noUsed') }}
          </div>
        </template>
      </v-container>
    </nice-dialog>
    <ik-entity-form
      v-model="showEditCouponDialog"
      :edited-index="activeCouponIndex"
      :model="CouponModel"
      :title="$t('editVouchers')"
      @need-refresh="reload"
    />
  </div>
</template>

<script>

import MemberCardRecord, { getCouponRecord } from '../../../../model/dataLayer/api/bills/CouponSalesRecord'
import NiceDialog from '@/views/widget/uiComponent/NiceDialog.vue'
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import { today } from '@/model/dataLayer/api/common/DateRepository'
import CouponInfoCard from '@/views/AadenPages/billAndReport/customer/CouponInfoCard.vue'
import Coupon from '@/model/dataLayer/api/bills/Coupon'
import { mapMutations } from 'vuex'
import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import IkEntityForm from '@/plugins/EntityFrameWork/IkEntityForm.vue'
import { predefinedDateRangeList } from '@/model/utils/DateRepository'

export default {
  name: 'SaledCouponListPage',
  components: {
    CouponInfoCard,
    PageHeader,
    NiceDialog,
    IkDataTable,
    IkEntityForm
  },
  data () {
    return {
      tab: 0,
      activeCouponIndex: null,
      couponList: [],
      CouponModel: Coupon,
      model: MemberCardRecord,
      couponDetail: null,
      loading: false,
      showCouponDetailDialog: null,
      dates: [today, today],
      showEditCouponDialog: null,
      showDetail: null,
      currentOrderId: null
    }
  },
  mounted () {
    this.dates = predefinedDateRangeList.at(-1).dateRange()
    this.reload()
  },
  methods: {
    ...mapMutations(['showBillDetail']),
    async deleteAndReload (dishId) {
      await Coupon.remove(dishId)
      await this.reload()
    },
    showOrder (orderId) {
      this.showBillDetail(orderId)
    },
    startEditCoupon (index) {
      this.showEditCouponDialog = true
      this.activeCouponIndex = index
    },
    async showCouponDetail (coupon) {
      this.couponDetail = await getCouponRecord(coupon.id)
      this.showCouponDetailDialog = true
    },
    async reload () {
      this.loading = true
      this.couponList = (await Coupon.getList(true)).filter(it => it.code !== 'gk' && it.code !== 'gel')
      this.loading = false
    }
  }
}
</script>
